import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import SeoHead, { LOCAL_BUSINESS_SCHEMA } from "@/components/public/SeoHead";
import { useReviewStats } from "@/hooks/useReviewStats";
import ReviewCard from "@/components/public/ReviewCard";
import PriceCalculator from "@/components/public/PriceCalculator";
import DevisForm from "@/components/public/DevisForm";
import { getLandingContent } from "./landingContent";
import { Star, Phone, MessageCircle, ChevronDown, CheckCircle, Shield, ArrowDown, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CALC_SLUGS = ["nettoyage-canape","nettoyage-chaises","nettoyage-fauteuils","nettoyage-matelas","nettoyage-moquette","nettoyage-tapis","nettoyage-terrasse","nettoyage-vitres"];

function parseSlug(combined) {
  // Pattern: nettoyage-XXX-ville or nettoyage-XXX-XXX-ville
  // Villes: lausanne, geneve, yverdon, montreux, nyon, fribourg, neuchatel, sion
  const villesSlugs = ["lausanne","geneve","yverdon","montreux","nyon","fribourg","neuchatel","sion"];
  const parts = combined.split("-");
  for (let i = parts.length - 1; i >= 1; i--) {
    const candidateVille = parts.slice(i).join("-");
    if (villesSlugs.includes(candidateVille)) {
      return { serviceSlug: parts.slice(0, i).join("-"), villeSlug: candidateVille };
    }
  }
  // Fallback: last segment is ville
  return { serviceSlug: parts.slice(0, -1).join("-"), villeSlug: parts[parts.length - 1] };
}

export default function LandingPage() {
  const { slug } = useParams();
  const { serviceSlug, villeSlug } = parseSlug(slug || "");
  const [service, setService] = useState(null);
  const [ville, setVille] = useState(null);
  const [avis, setAvis] = useState([]);
  const [loading, setLoading] = useState(true);
  const formRef = useRef(null);
  const reviewStats = useReviewStats();

  useEffect(() => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/services/${serviceSlug}`).catch(() => null),
      axios.get(`${API}/villes/${villeSlug}`).catch(() => null),
      axios.get(`${API}/avis`).catch(() => ({ data: [] })),
    ]).then(([s, v, a]) => {
      setService(s?.data || null);
      setVille(v?.data || null);
      setAvis(a?.data || []);
      setLoading(false);
    });
  }, [serviceSlug, villeSlug]);

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth" });

  if (loading) return (
    <div className="min-h-screen bg-[#062133] flex items-center justify-center">
      <Loader2 className="w-10 h-10 animate-spin text-white/60" />
    </div>
  );
  if (!service || !ville) return (
    <div className="min-h-screen flex items-center justify-center text-[#062133]/60">
      <div className="text-center"><p className="text-lg mb-4">Page non trouvée</p><Link to="/" className="text-[#336DBF] font-semibold hover:underline">Retour à l'accueil</Link></div>
    </div>
  );

  const villeName = ville.name;
  const serviceName = service.name;
  const content = getLandingContent(serviceSlug);
  const faq = service.copywriting?.faq || [];
  const isCalc = CALC_SLUGS.includes(serviceSlug);
  const villeAvis = avis.filter(a => a.ville === villeName);
  const displayAvis = villeAvis.length >= 3 ? villeAvis.slice(0, 4) : avis.slice(0, 4);

  const serviceSchema = {
    "@context": "https://schema.org", "@type": "Service",
    "serviceType": serviceName,
    "provider": { "@type": "LocalBusiness", "@id": "https://www.nettoyage-expert.ch/#business" },
    "areaServed": { "@type": "City", "name": villeName },
    "description": `${serviceName} professionnel à ${villeName} par Nettoyage Expert, entreprise notée 5 étoiles sur Google.`
  };

  const schemas = [serviceSchema];
  if (faq.length > 0) {
    schemas.push({
      "@context": "https://schema.org", "@type": "FAQPage",
      "mainEntity": faq.map(f => ({ "@type": "Question", "name": f.question, "acceptedAnswer": { "@type": "Answer", "text": f.réponse || f.reponse || "" } }))
    });
  }

  return (
    <div className="min-h-screen" data-testid="landing-page">
      <SeoHead
        title={`${serviceName} à ${villeName} | Devis Gratuit`}
        description={`Nettoyage professionnel de ${serviceName.toLowerCase().replace("nettoyage ","")} à ${villeName}. Résultat visible immédiatement. 5.0/5 sur Google. Devis gratuit sous 2h. 024 539 10 33`}
        path={`/devis/${slug}`}
        schemas={schemas}
      />

      {/* HEADER simplifié */}
      <header className="bg-white border-b border-[#062133]/10 sticky top-0 z-50" data-testid="landing-header">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/"><img src="/logo-black.png" alt="Nettoyage Expert" className="h-[50px] sm:h-[60px] w-auto" /></Link>
          <div className="flex items-center gap-2 sm:gap-3">
            <a href="tel:+41245391033" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-[#062133]"><Phone size={16} /> 024 539 10 33</a>
            <a href="https://wa.me/41786202854" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#25D366] text-white text-sm font-semibold rounded-md"><MessageCircle size={16} /> <span className="hidden sm:inline">WhatsApp</span></a>
          </div>
        </div>
      </header>

      {/* SECTION 1 — HERO */}
      <section className="bg-gradient-to-br from-[#062133] to-[#336DBF] py-16 sm:py-20 lg:py-24" data-testid="landing-hero">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-full px-4 py-1.5 mb-6">
            <Star size={14} className="fill-amber-400 text-amber-400" />
            <span className="text-white/90 text-sm font-medium">5.0/5 sur Google • {reviewStats.count} avis • Intervention rapide</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight leading-tight" data-testid="landing-h1">
            {serviceName} à {villeName} — Résultat garanti
          </h1>
          <p className="mt-5 text-lg sm:text-xl text-white/80 max-w-2xl mx-auto leading-relaxed">{content.heroSub}</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <button onClick={scrollToForm} className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-[#062133] font-bold rounded-lg hover:bg-white/90 transition-colors text-base sm:text-lg" data-testid="hero-cta-devis">
              Obtenir mon devis gratuit <ArrowDown size={18} />
            </button>
            <a href="tel:+41245391033" className="inline-flex items-center justify-center gap-2 px-6 py-4 border-2 border-white/30 text-white font-semibold rounded-lg hover:bg-white/10 transition-colors">
              <Phone size={18} /> Appeler maintenant
            </a>
          </div>
        </div>
      </section>

      {/* SECTION 2 — PERTINENCE */}
      <section className="py-14 sm:py-20 bg-white" data-testid="landing-pertinence">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#062133] tracking-tight mb-8">{content.pertinence.title.replace("[Ville]", villeName).replace("[service]", serviceName.toLowerCase().replace("nettoyage ",""))}</h2>
          <div className="space-y-4">
            {content.pertinence.points.map((p, i) => (
              <div key={i} className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-[#336DBF] shrink-0 mt-0.5" />
                <p className="text-[#062133]/80 text-base sm:text-lg">{p}</p>
              </div>
            ))}
          </div>
          <p className="mt-8 text-[#062133] font-semibold text-lg">Si vous avez coché au moins une case — il est temps d'agir.</p>
        </div>
      </section>

      {/* SECTION 3 — ÉMOTION */}
      <section className="py-14 sm:py-20 bg-[#F4F6F8]" data-testid="landing-emotion">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#062133] tracking-tight mb-8">{content.emotion.title}</h2>
          <div className="space-y-4">
            {content.emotion.points.map((p, i) => (
              <div key={i} className="flex items-start gap-3">
                <span className="text-red-500 font-bold text-lg mt-0.5 shrink-0">!</span>
                <p className="text-[#062133]/80 text-base sm:text-lg">{p}</p>
              </div>
            ))}
          </div>
          <p className="mt-8 text-[#062133] font-semibold text-lg italic">Chaque jour qui passe aggrave le problème.</p>
        </div>
      </section>

      {/* CTA intermédiaire */}
      <section className="py-10 sm:py-14 bg-[#336DBF]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-white text-xl sm:text-2xl font-bold mb-4">Ne laissez pas la situation se dégrader</p>
          <button onClick={scrollToForm} className="inline-flex items-center gap-2 px-8 py-4 bg-white text-[#336DBF] font-bold rounded-lg hover:bg-white/90 transition-colors text-base sm:text-lg">
            Obtenir mon devis gratuit <ArrowDown size={18} />
          </button>
        </div>
      </section>

      {/* SECTION 4 — DÉSIR */}
      <section className="py-14 sm:py-20 bg-white" data-testid="landing-desir">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#062133] tracking-tight mb-8">{content.desir.title}</h2>
          <div className="space-y-4">
            {content.desir.points.map((p, i) => (
              <div key={i} className="flex items-start gap-3">
                <Star className="w-5 h-5 text-amber-400 fill-amber-400 shrink-0 mt-1" />
                <p className="text-[#062133]/80 text-base sm:text-lg">{p}</p>
              </div>
            ))}
          </div>
          <p className="mt-8 text-[#336DBF] font-semibold text-lg">Nos clients à {villeName} sont unanimes : le résultat dépasse leurs attentes.</p>
        </div>
      </section>

      {/* SECTION 5 — PREUVE SOCIALE */}
      <section className="py-14 sm:py-20 bg-[#F4F6F8]" data-testid="landing-avis">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#062133] tracking-tight">Ce que disent nos clients {villeAvis.length >= 3 ? `à ${villeName}` : "en Suisse romande"}</h2>
            <div className="mt-3 flex items-center justify-center gap-2">
              <div className="flex gap-0.5">{Array.from({length:5},(_,i)=><Star key={i} size={20} className="fill-amber-400 text-amber-400" />)}</div>
              <span className="text-[#062133]/60 text-lg font-medium">5.0/5 — {reviewStats.count} avis Google</span>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {displayAvis.map((a, i) => <ReviewCard key={a.id || i} avis={a} />)}
          </div>
          <div className="mt-6 text-center">
            <a href="https://g.page/r/CTTcLCQur3ryEAE" target="_blank" rel="noopener noreferrer" className="text-sm text-[#336DBF] font-medium hover:underline">Voir tous nos avis sur Google</a>
          </div>
        </div>
      </section>

      {/* CTA intermédiaire */}
      <section className="py-10 sm:py-14 bg-[#336DBF]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-white text-xl sm:text-2xl font-bold mb-4">Rejoignez nos 35 clients satisfaits</p>
          <button onClick={scrollToForm} className="inline-flex items-center gap-2 px-8 py-4 bg-white text-[#336DBF] font-bold rounded-lg hover:bg-white/90 transition-colors text-base sm:text-lg">
            Je veux le même résultat <ArrowDown size={18} />
          </button>
        </div>
      </section>

      {/* SECTION 6 — MÉTHODE */}
      <section className="py-14 sm:py-20 bg-white" data-testid="landing-methode">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#062133] tracking-tight mb-10 text-center">Notre méthode en 4 étapes</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {content.methode.steps.map((s, i) => (
              <div key={i} className="text-center p-6 border border-[#062133]/10 rounded-xl">
                <div className="w-12 h-12 rounded-full bg-[#336DBF] text-white font-bold text-xl flex items-center justify-center mx-auto mb-4">{s.num}</div>
                <h3 className="font-bold text-[#062133] mb-2">{s.title}</h3>
                <p className="text-sm text-[#062133]/60">{s.desc}</p>
              </div>
            ))}
          </div>
          <p className="mt-8 text-center text-[#062133]/60 text-sm">{content.methode.footer}</p>
        </div>
      </section>

      {/* SECTION 7 — CALCULATEUR */}
      {isCalc && (
        <section className="py-14 sm:py-20 bg-[#F4F6F8]" data-testid="landing-calculator">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#062133] tracking-tight mb-8 text-center">Estimez votre nettoyage à {villeName}</h2>
            <PriceCalculator preselectedSlug={serviceSlug} />
          </div>
        </section>
      )}

      {/* SECTION 8 — FORMULAIRE */}
      <section ref={formRef} className="py-14 sm:py-20 bg-white" id="devis-form" data-testid="landing-form">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#062133] tracking-tight">Demandez votre devis gratuit maintenant</h2>
            <p className="mt-2 text-[#062133]/60 text-lg">Réponse sous 2 heures • Sans engagement • Intervention rapide à {villeName}</p>
          </div>
          <div className="bg-[#F4F6F8] border border-[#062133]/10 rounded-xl p-6 sm:p-8">
            <DevisForm source="ads_landing" prefilledService={serviceName} prefilledVille={villeName} />
          </div>
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-4 text-sm text-[#062133]/60">
            <div className="flex items-center gap-1.5"><Shield size={14} /> Vos données sont protégées</div>
            <a href="tel:+41245391033" className="flex items-center gap-1.5 hover:text-[#336DBF]"><Phone size={14} /> 024 539 10 33</a>
            <a href="https://wa.me/41786202854" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 hover:text-[#25D366]"><MessageCircle size={14} /> WhatsApp</a>
          </div>
        </div>
      </section>

      {/* SECTION 9 — FAQ */}
      {faq.length > 0 && (
        <section className="py-14 sm:py-20 bg-[#F4F6F8]" data-testid="landing-faq">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#062133] tracking-tight mb-8">Questions fréquentes — {serviceName} à {villeName}</h2>
            <div className="space-y-3">
              {faq.map((item, i) => (
                <details key={i} className="group bg-white border border-[#062133]/10 rounded-lg" data-testid={`landing-faq-${i}`}>
                  <summary className="flex items-center justify-between cursor-pointer p-5 list-none [&::-webkit-details-marker]:hidden">
                    <span className="font-medium text-[#062133] pr-4">{item.question}</span>
                    <ChevronDown className="w-5 h-5 text-[#062133]/60 shrink-0 transition-transform group-open:rotate-180" />
                  </summary>
                  <div className="px-5 pb-5 pt-0"><p className="text-[#062133]/60 leading-relaxed">{item.réponse || item.reponse}</p></div>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* SECTION 10 — DERNIER CTA */}
      <section className="py-14 sm:py-20 bg-[#062133]" data-testid="landing-final-cta">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-white text-2xl sm:text-3xl font-bold mb-3">Votre {serviceName.toLowerCase().replace("nettoyage ","").replace("nettoyage ","")} mérite un professionnel</p>
          <div className="flex items-center justify-center gap-2 mb-6">
            <div className="flex gap-0.5">{Array.from({length:5},(_,i)=><Star key={i} size={16} className="fill-amber-400 text-amber-400" />)}</div>
            <span className="text-white/70 text-sm">5.0/5 sur Google — {reviewStats.count} avis — Intervention rapide à {villeName}</span>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button onClick={scrollToForm} className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#336DBF] text-white font-bold rounded-lg hover:bg-[#2A5A9E] transition-colors text-base sm:text-lg">
              Demander mon devis gratuit <ArrowDown size={18} />
            </button>
            <a href="tel:+41245391033" className="inline-flex items-center justify-center gap-2 px-6 py-4 border border-white/30 text-white font-semibold rounded-lg hover:bg-white/10 transition-colors">
              <Phone size={18} /> 024 539 10 33
            </a>
            <a href="https://wa.me/41786202854" target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-6 py-4 border border-white/30 text-white font-semibold rounded-lg hover:bg-white/10 transition-colors">
              <MessageCircle size={18} /> WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* Footer simplifié */}
      <footer className="bg-[#062133] border-t border-white/10 py-6">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-white/50 text-xs">Nettoyage Expert — Rue Jean-André Venel 4, 1400 Yverdon-les-Bains</p>
          <p className="text-white/50 text-xs mt-1">024 539 10 33 | info@nettoyage-expert.ch | <Link to="/" className="hover:text-white/80 underline">nettoyage-expert.ch</Link></p>
        </div>
      </footer>

      {/* Sticky bar mobile */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-[#062133]/10 p-2 flex gap-2 sm:hidden z-50" data-testid="landing-sticky-mobile">
        <a href="tel:+41245391033" className="flex-1 inline-flex items-center justify-center gap-1.5 py-3 bg-[#336DBF] text-white font-semibold rounded-lg text-sm">
          <Phone size={16} /> Appeler
        </a>
        <a href="https://wa.me/41786202854" target="_blank" rel="noopener noreferrer" className="flex-1 inline-flex items-center justify-center gap-1.5 py-3 bg-[#25D366] text-white font-semibold rounded-lg text-sm">
          <MessageCircle size={16} /> WhatsApp
        </a>
      </div>
    </div>
  );
}
